import { useModalPortalTarget } from '@amelio/ui-library';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { AssignReviewer } from './components/AssignReviewer';
import { LockedAdditionsConfirmDialog } from './components/LockedAdditionsConfirmDialog';
import { SubmittedRemovalConfirmDialog } from './components/SubmittedRemovalConfirmDialog';
import { useAssignReviewerData } from './hooks/useAssignReviewerData';
import { useConfirmLockedAdditions } from './hooks/useConfirmLockedAdditions';
import { useConfirmSubmittedRemoval } from './hooks/useConfirmSubmittedRemoval';
import { useReviewerAssignments } from './hooks/useReviewerAssignments';
import { getReviewersErrorMessage } from './reviewersErrorMessage';
import {
    AssignReviewerFormFields,
    assignReviewerSchema,
    type AssignReviewerFormData,
    type ReviewerAssignment,
} from './validation';

import type { TranslationKey } from '@/i18n-key';
import type { Evaluation360FormNavigatorParams } from '@/navigation/Evaluation360/Evaluation360FormNavigator';

import {
    useUpdateReviewAudience,
    useUpdateReviewEvaluatorSelection,
} from '@/api/review-services/review';
import { assignReviewerOnNextAtom } from '@/atoms/evaluation360';
import { CycleInstanceStatus } from '@/constants/review/CycleInstance';
import { NEW_FEEDBACK_360_COMMENT_REPLIES_ENABLED } from '@/constants/review/Feedback360Settings';
import { FeedbackPrivacy } from '@/constants/review/FeedbackPrivacy';
import { useAmelioTranslations } from '@/hooks';
import { useFetchErrorsHandler } from '@/hooks/api/useFetchErrorsHandler';
import { useError } from '@/hooks/utils/useError';
import { useRegisterPageAction } from '@/hooks/utils/useRegisterPageAction';
import {
    getFeedback360SettingsLock,
    isReviewerAdditionLocked,
    isReviewerRemovalLocked,
} from '@/utils/cycleInstance';
import { rememberFocus } from '@/utils/focus';
import {
    buildReviewEvaluatorSelection,
    getAddedReviewers,
    type Reviewee,
} from '@/utils/reviewEvaluatorSelection';
import { validationMessageKey } from '@/utils/validationMessage';

export const AssignReviewerPage = () => {
    const { t, i18n } = useAmelioTranslations();
    const { handleError } = useError();
    const portalTarget = useModalPortalTarget();
    const { cycleInstanceId } = useParams<Evaluation360FormNavigatorParams>();

    const { cycleInstance, baseReviewees, isLoading, fetchErrors } =
        useAssignReviewerData(cycleInstanceId);

    const { mutateAsync: updateReviewEvaluatorSelection, isPending: isUpdatingReviewers } =
        useUpdateReviewEvaluatorSelection();

    const { mutateAsync: updateReviewAudience, isPending: isRemovingReviewee } =
        useUpdateReviewAudience();

    const { setValue, trigger, reset, getFieldState, getValues, watch } =
        useForm<AssignReviewerFormData>({
            resolver: zodResolver(assignReviewerSchema),
            defaultValues: {
                [AssignReviewerFormFields.reviewerAssignments]: [],
                [AssignReviewerFormFields.feedbackPrivacy]: FeedbackPrivacy.NAMED,
                [AssignReviewerFormFields.commentRepliesEnabled]:
                    NEW_FEEDBACK_360_COMMENT_REPLIES_ENABLED,
            },
        });

    // Why "Suivant" refused the reviewers, said under them until they or the mode change.
    const [reviewersMessageKey, setReviewersMessageKey] = useState<TranslationKey>();

    // Read from the server once per 360; like the reviewers, settings picked since survive a refetch.
    // The step stays mounted when another 360 opens on it, which must bring its own settings.
    const settingsHydratedForRef = useRef<string | undefined>(undefined);
    const reviewerAssignments = watch(AssignReviewerFormFields.reviewerAssignments);
    const feedbackPrivacy = watch(AssignReviewerFormFields.feedbackPrivacy);
    const commentRepliesEnabled = watch(AssignReviewerFormFields.commentRepliesEnabled);
    const feedback360SettingsLock = getFeedback360SettingsLock(cycleInstance);
    const isRemovalLocked = isReviewerRemovalLocked(cycleInstance);

    const reviewees: Reviewee[] = useMemo(() => {
        return baseReviewees.map((reviewee) => ({
            ...reviewee,
            reviewers:
                reviewerAssignments.find((a) => a.revieweeId === reviewee.id)?.reviewers ??
                reviewee.reviewers,
            lockedReviewerIds: isRemovalLocked ? reviewee.reviewers.map(({ id }) => id) : [],
        }));
    }, [baseReviewees, reviewerAssignments, isRemovalLocked]);

    const clearReviewersMessage = useCallback(() => setReviewersMessageKey(undefined), []);

    const { addReviewers, removeReviewer } = useReviewerAssignments({
        baseReviewees,
        getValues,
        setValue,
        onChange: clearReviewersMessage,
    });

    const {
        pending: pendingSubmittedRemoval,
        remove: onRemoveReviewer,
        answer: answerSubmittedRemoval,
    } = useConfirmSubmittedRemoval({
        reviewees,
        reviewEvaluatorSelection: cycleInstance?.reviewEvaluatorSelection,
        removeReviewer,
    });

    const onRemoveReviewee = useCallback(
        async (revieweeId: string) => {
            if (!cycleInstance?.reviewAudience) {
                return;
            }

            const updatedAssignments =
                cycleInstance.reviewAudience.reviewAudienceAssignments.filter(
                    (assignment) => assignment.assignedTo !== revieweeId,
                );

            try {
                await updateReviewAudience({
                    ...cycleInstance.reviewAudience,
                    reviewAudienceAssignments: updatedAssignments,
                });

                const previousAssignments =
                    getValues(AssignReviewerFormFields.reviewerAssignments) || [];

                setValue(
                    AssignReviewerFormFields.reviewerAssignments,
                    previousAssignments.filter(
                        (assignment) => assignment.revieweeId !== revieweeId,
                    ),
                    { shouldValidate: false, shouldDirty: true },
                );
                clearReviewersMessage();
            } catch (error) {
                handleError({ error });
            }
        },
        [
            cycleInstance?.reviewAudience,
            updateReviewAudience,
            getValues,
            setValue,
            clearReviewersMessage,
            handleError,
        ],
    );

    const {
        pending: pendingAdditions,
        confirm: confirmAdditions,
        answer: answerAdditions,
    } = useConfirmLockedAdditions();

    const onNext = useCallback(async () => {
        const restoreFocus = rememberFocus(portalTarget ?? undefined);
        const isValid = await trigger(AssignReviewerFormFields.reviewerAssignments);

        if (!isValid) {
            const fieldState = getFieldState(AssignReviewerFormFields.reviewerAssignments);
            setReviewersMessageKey(
                validationMessageKey(fieldState.error?.message) ??
                    'evaluation360AssignReviewers.validation.minReviewers',
            );

            return false;
        }

        setReviewersMessageKey(undefined);

        if (
            isRemovalLocked &&
            !(await confirmAdditions(
                getAddedReviewers(
                    getValues(AssignReviewerFormFields.reviewerAssignments),
                    baseReviewees,
                ),
            ))
        ) {
            restoreFocus();
            return false;
        }

        try {
            await updateReviewEvaluatorSelection(
                buildReviewEvaluatorSelection({ cycleInstance, ...getValues() }),
            );

            return true;
        } catch (error) {
            handleError({ error });

            return false;
        }
    }, [
        trigger,
        getFieldState,
        portalTarget,
        cycleInstance,
        isRemovalLocked,
        confirmAdditions,
        baseReviewees,
        getValues,
        handleError,
    ]);

    useRegisterPageAction(assignReviewerOnNextAtom, onNext);

    useFetchErrorsHandler({
        errors: fetchErrors,
    });

    useEffect(() => {
        if (!baseReviewees.length || !cycleInstance?.reviewEvaluatorSelection) {
            return;
        }

        // The cycle is refetched after a reviewee is removed: reviewers picked for the other
        // reviewees are only saved on "Next", so they must survive the new server snapshot.
        const unsavedAssignments = getValues(AssignReviewerFormFields.reviewerAssignments);

        const normalizedAssignments: ReviewerAssignment[] = baseReviewees.map((reviewee) => {
            const unsavedAssignment = unsavedAssignments.find(
                (assignment) =>
                    assignment.reviewAudienceAssignmentId === reviewee.reviewAudienceAssignmentId,
            );

            return {
                revieweeId: reviewee.id,
                reviewers: unsavedAssignment?.reviewers ?? reviewee.reviewers,
                reviewAudienceAssignmentId: reviewee.reviewAudienceAssignmentId,
            };
        });

        const areSettingsHydrated = settingsHydratedForRef.current === cycleInstance.id;

        reset({
            [AssignReviewerFormFields.reviewerAssignments]: normalizedAssignments,
            [AssignReviewerFormFields.feedbackPrivacy]: areSettingsHydrated
                ? getValues(AssignReviewerFormFields.feedbackPrivacy)
                : cycleInstance.feedbackPrivacy,
            [AssignReviewerFormFields.commentRepliesEnabled]: areSettingsHydrated
                ? getValues(AssignReviewerFormFields.commentRepliesEnabled)
                : cycleInstance.commentRepliesEnabled,
        });
        settingsHydratedForRef.current = cycleInstance.id;
    }, [baseReviewees, cycleInstance, getValues, reset]);

    const isEvaluationLaunched = cycleInstance?.status === CycleInstanceStatus.LAUNCHED;

    return (
        <>
            <AssignReviewer
                reviewees={reviewees}
                isLoading={isLoading}
                isSaving={isUpdatingReviewers || isRemovingReviewee}
                isEvaluationLaunched={isEvaluationLaunched}
                isReviewerRemovalLocked={isRemovalLocked}
                isReviewerAdditionLocked={isReviewerAdditionLocked(cycleInstance)}
                feedbackPrivacy={feedbackPrivacy}
                feedback360SettingsLock={feedback360SettingsLock}
                onFeedbackPrivacyChange={(value) => {
                    setValue(AssignReviewerFormFields.feedbackPrivacy, value);
                    clearReviewersMessage();
                }}
                reviewersErrorMessage={getReviewersErrorMessage({
                    messageKey: reviewersMessageKey,
                    reviewees,
                    feedbackPrivacy,
                    t,
                    language: i18n.language,
                })}
                commentRepliesEnabled={commentRepliesEnabled}
                onCommentRepliesEnabledChange={(value) =>
                    setValue(AssignReviewerFormFields.commentRepliesEnabled, value)
                }
                onAddReviewer={addReviewers}
                onRemoveReviewer={onRemoveReviewer}
                onRemoveReviewee={onRemoveReviewee}
            />

            <LockedAdditionsConfirmDialog
                reviewers={pendingAdditions}
                onAnswer={answerAdditions}
            />

            <SubmittedRemovalConfirmDialog
                reviewer={pendingSubmittedRemoval}
                onAnswer={answerSubmittedRemoval}
            />
        </>
    );
};
